import { memo, useEffect, useState } from "react";

import { Brain } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { supabase } from "@/integrations/supabase/client";
import { getCategoryConfig } from "@/lib/memoryCategories";

interface Memory {
  id: string;
  content: string;
  category: string;
  workspace_id?: string;
  board_id?: string;
  folder_id?: string;
  tag_id?: string;
  project_id?: string;
  enabled: boolean;
}

interface ActiveMemoriesIndicatorProps {
  projectId: string;
}

const ActiveMemoriesIndicatorComponent = ({ projectId }: ActiveMemoriesIndicatorProps) => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [contextInfo, setContextInfo] = useState<{
    workspace_id?: string;
    board_id?: string;
    folder_id?: string;
  }>({});

  useEffect(() => {
    fetchActiveMemories();
  }, [projectId]);

  const fetchActiveMemories = async () => {
    setLoading(true);
    try {
      // First, get the project's context (workspace, board, folder, tags)
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select(`
          id,
          board_id,
          folder_id,
          boards!inner(
            id,
            workspace_id
          )
        `)
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;

      const workspace_id = projectData.boards.workspace_id;
      const board_id = projectData.board_id;
      const folder_id = projectData.folder_id;

      setContextInfo({ workspace_id, board_id, folder_id });

      // Get tags for this project
      const { data: tagData } = await supabase
        .from("project_tags")
        .select("tag_id")
        .eq("project_id", projectId);

      const tagIds = tagData?.map(t => t.tag_id) || [];

      // Fetch all applicable memories (workspace, board, folder, tags, project)
      const memoryQueries = [];

      // Workspace memories
      if (workspace_id) {
        memoryQueries.push(
          supabase
            .from("memories")
            .select("*")
            .eq("workspace_id", workspace_id)
            .eq("enabled", true)
        );
      }

      // Board memories
      if (board_id) {
        memoryQueries.push(
          supabase
            .from("memories")
            .select("*")
            .eq("board_id", board_id)
            .eq("enabled", true)
        );
      }

      // Folder memories
      if (folder_id) {
        memoryQueries.push(
          supabase
            .from("memories")
            .select("*")
            .eq("folder_id", folder_id)
            .eq("enabled", true)
        );
      }

      // Tag memories
      if (tagIds.length > 0) {
        memoryQueries.push(
          supabase
            .from("memories")
            .select("*")
            .in("tag_id", tagIds)
            .eq("enabled", true)
        );
      }

      // Project memories
      memoryQueries.push(
        supabase
          .from("memories")
          .select("*")
          .eq("project_id", projectId)
          .eq("enabled", true)
      );

      const results = await Promise.all(memoryQueries);
      const allMemories = results
        .flatMap(result => result.data || [])
        .filter((memory, index, self) => 
          index === self.findIndex(m => m.id === memory.id)
        ) as Memory[];

      setMemories(allMemories);
    } catch (error) {
      console.error("Error fetching active memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const getScopeLabel = (memory: Memory) => {
    if (memory.workspace_id) return "Workspace";
    if (memory.board_id) return "Board";
    if (memory.folder_id) return "Folder";
    if (memory.tag_id) return "Tag";
    if (memory.project_id) return "Project";
    return "Unknown";
  };

  const getScopeColor = (memory: Memory) => {
    if (memory.workspace_id) return "oklch(0.75 0.15 270)"; // Purple
    if (memory.board_id) return "oklch(0.65 0.2 240)"; // Blue
    if (memory.folder_id) return "oklch(0.70 0.18 180)"; // Cyan
    if (memory.tag_id) return "oklch(0.72 0.20 150)"; // Green
    if (memory.project_id) return "oklch(0.65 0.20 30)"; // Orange
    return "oklch(0.65 0.1 0)"; // Gray
  };

  if (loading || memories.length === 0) return null;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/50 rounded-md border border-border cursor-pointer hover:bg-accent/70 transition-colors">
          <Brain className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {memories.length} {memories.length === 1 ? "memory" : "memories"} active
          </span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-96 max-h-96 overflow-y-auto" align="start">
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-border">
            <Brain className="h-4 w-4" />
            <h4 className="font-semibold text-sm">Active Memories</h4>
          </div>
          
          {/* Group memories by scope */}
          {["workspace_id", "board_id", "folder_id", "tag_id", "project_id"].map((scopeKey) => {
            const scopeMemories = memories.filter((m) => {
              if (scopeKey === "workspace_id") return m.workspace_id;
              if (scopeKey === "board_id") return m.board_id;
              if (scopeKey === "folder_id") return m.folder_id;
              if (scopeKey === "tag_id") return m.tag_id;
              if (scopeKey === "project_id") return m.project_id;
              return false;
            });

            if (scopeMemories.length === 0) return null;

            const getScopeInfo = () => {
              if (scopeKey === "workspace_id") return { label: "Workspace", color: "oklch(0.75 0.15 270)" };
              if (scopeKey === "board_id") return { label: "Board", color: "oklch(0.65 0.2 240)" };
              if (scopeKey === "folder_id") return { label: "Folder", color: "oklch(0.70 0.18 180)" };
              if (scopeKey === "tag_id") return { label: "Tags", color: "oklch(0.72 0.20 150)" };
              if (scopeKey === "project_id") return { label: "Project", color: "oklch(0.65 0.20 30)" };
              return { label: "Unknown", color: "oklch(0.65 0.1 0)" };
            };

            const scopeInfo = getScopeInfo();

            return (
              <div key={scopeKey} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="text-xs"
                    style={{
                      backgroundColor: `${scopeInfo.color}15`,
                      color: scopeInfo.color,
                      borderColor: `${scopeInfo.color}40`,
                    }}
                  >
                    {scopeInfo.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {scopeMemories.length} {scopeMemories.length === 1 ? "memory" : "memories"}
                  </span>
                </div>
                <div className="space-y-1 ml-2 pl-2 border-l-2" style={{ borderColor: `${scopeInfo.color}40` }}>
                  {scopeMemories.map((memory) => {
                    const categoryConfig = getCategoryConfig(memory.category as any);
                    const CategoryIcon = categoryConfig.icon;

                    return (
                      <div key={memory.id} className="p-2 bg-muted rounded-md space-y-2">
                        <Badge
                          variant="outline"
                          className="gap-1 text-xs"
                          style={{
                            borderColor: categoryConfig.color,
                            color: categoryConfig.color,
                          }}
                        >
                          <CategoryIcon className="h-3 w-3" />
                          {categoryConfig.label}
                        </Badge>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {memory.content}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Memories cascade from parent scopes (workspace → board → folder → project)
            </p>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export const ActiveMemoriesIndicator = memo(ActiveMemoriesIndicatorComponent);
